import styled, { keyframes } from "styled-components";
import { colors } from "../../../../styles/colors";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-6px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
    opacity: 0.2;
  }
`;

const linkGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 8px ${colors.blueAlpha});
  }
  50% {
    filter: drop-shadow(0 0 16px ${colors.blueAlpha}) drop-shadow(0 0 24px ${colors.blueAlpha});
  }
`;

export const NoJoinLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 30px;
  text-align: center;
  direction: rtl;
  background: linear-gradient(
    135deg,
    ${colors.lightGray} 0%,
    ${colors.lightBlueGrayBackground} 50%,
    ${colors.lightGray} 100%
  );
  border-radius: 12px;
  border: 1px solid ${colors.lightBlueGrayAlpha};
  box-shadow: 0 3px 15px ${colors.lightBlueGrayAlpha40},
    0 1px 3px ${colors.lightBlueGrayAlpha};
  animation: ${fadeIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 200px;
  margin-top: 16px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${colors.lightBlueGrayAlpha40},
      transparent
    );
    animation: ${shimmer} 4s ease-in-out infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: -30%;
    left: -30%;
    width: 160%;
    height: 160%;
    background: radial-gradient(
      circle,
      ${colors.lightBlueGrayAlpha} 0%,
      transparent 60%
    );
    animation: ${float} 8s ease-in-out infinite;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
    min-height: 180px;
  }
`;

export const NoJoinLinksIcon = styled.div`
  margin-bottom: 20px;
  animation: ${float} 3s ease-in-out infinite;
  position: relative;
  z-index: 2;
  opacity: 0.9;
  transition: all 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: radial-gradient(
      circle,
      ${colors.blueAlpha} 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: -1;
    animation: ${pulse} 2.5s ease-in-out infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background: linear-gradient(
      135deg,
      ${colors.lightBlueGrayAlpha40} 0%,
      transparent 50%
    );
    border-radius: 50%;
    z-index: -1;
    animation: ${float} 5s ease-in-out infinite reverse;
  }

  &:hover {
    opacity: 1;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;

    &::before {
      width: 70px;
      height: 70px;
    }

    &::after {
      width: 50px;
      height: 50px;
    }
  }
`;

export const LinkIcon = styled.div`
  width: 48px;
  height: 48px;
  position: relative;
  z-index: 2;
  animation: ${linkGlow} 2s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, ${colors.blue} 0%, ${colors.teal} 100%);
    border-radius: 8px;
    transform: rotate(45deg);
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    width: 20px;
    height: 20px;
    background: ${colors.white};
    border-radius: 2px;
    box-shadow: 0 0 0 2px ${colors.white};
  }

  /* Link chain effect */
  &::before {
    box-shadow: 0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.blueAlpha},
      0 4px 12px ${colors.blueAlpha};
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    &::after {
      width: 16px;
      height: 16px;
    }
  }
`;

export const NoJoinLinksText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.darkBlue};
  font-family: "Noto Sans Hebrew", sans-serif;
  margin: 0 0 10px 0;
  line-height: 1.4;
  letter-spacing: 0.2px;
  position: relative;
  z-index: 2;
  max-width: 280px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 8px;
  }
`;

export const NoJoinLinksSubtext = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.blueGray};
  font-family: "Noto Sans Hebrew", sans-serif;
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.1px;
  position: relative;
  z-index: 2;
  max-width: 240px;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
